/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import {
  EmbeddedViewRef,
  EventEmitter,
  Injectable,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { domOffset, domOffsetUntil } from '@gh/core-util';
import { find } from 'lodash';

const DND_OFFSET = -3;

export interface VirtualDndDragStartedEvent<T = any> {
  data: T;

  setPreviewData(data: any): void;
}

export interface VirtualDndDragEndedEvent<I = any, Z = any> {
  itemData: I;
  zoneData?: Z;
}

export interface VirtualDndDragMovedEvent<T = any> {
  data: T;
  pagePosition: VirtualDndPosition;
  viewportPosition: VirtualDndPosition;
}

export interface VirtualDndZoneEvent<I = any, Z = any> {
  itemData: I;
  zoneData: Z;
}

export interface VirtualDndPosition {
  x: number;
  y: number;
}

interface VirtualDndBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface VirtualDndPreview {
  templateRef: TemplateRef<any>;
  viewContainerRef: ViewContainerRef;
}

export interface VirtualDndViewport {
  element: HTMLElement;
  preview: VirtualDndPreview;
  touchDelay: number;
  disabled: boolean;
  dragStarted: EventEmitter<VirtualDndDragStartedEvent>;
  dragMoved: EventEmitter<VirtualDndDragMovedEvent>;
  dragEnded: EventEmitter<VirtualDndDragEndedEvent>;
  zoneEntered: EventEmitter<VirtualDndZoneEvent>;
  zoneExited: EventEmitter<VirtualDndZoneEvent>;
}

interface VirtualDndZone {
  element: HTMLElement;
  data: any;
  box?: VirtualDndBox;
}

export const passiveEventListenerOptions = normalizePassiveListenerOptions({ passive: true });
export const activeEventListenerOptions = normalizePassiveListenerOptions({ passive: false });
export const activeCapturingEventOptions = normalizePassiveListenerOptions({
  passive: false,
  capture: true,
});
export const passiveCapturingEventOptions = normalizePassiveListenerOptions({
  passive: true,
  capture: true,
});

function isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
  return event.type.startsWith('touch');
}

function getPointerPosition(event: MouseEvent | TouchEvent): VirtualDndPosition {
  const point = isTouchEvent(event) ? (event.touches[0] || event.changedTouches[0]) : event;

  return {
    x: point.pageX,
    y: point.pageY,
  };
}

function getElementOffset(element: HTMLElement, position: VirtualDndPosition): VirtualDndPosition {
  const elementRect = element.getBoundingClientRect();
  return {
    x: position.x - elementRect.left,
    y: position.y - elementRect.top,
  };
}

function shiftPosition({ x, y }: VirtualDndPosition, shift: number): VirtualDndPosition {
  return {
    x: x + shift,
    y: y + shift,
  };
}

function toPosition({ left, top }: { left: number; top: number }): VirtualDndPosition {
  return { x: left, y: top };
}

@Injectable()
export class VirtualDndService implements OnDestroy {
  private viewport: VirtualDndViewport;
  private zones: VirtualDndZone[] = [];
  private activeZone?: VirtualDndZone;
  private draggingItemData: any;
  private isDragging: boolean = false;
  private pointerDownWhen?: Date;
  private previewRef?: EmbeddedViewRef<any>;
  private previewEl?: HTMLElement;
  private viewportOffset: VirtualDndPosition;
  private elementOffset: VirtualDndPosition;
  // On touch devices user can accidentally touch DnD handle while scrolling.
  // We use touch start delay for DnD handle to avoid issue while scrolling:
  // When user starts touch he should wait until the delay does not pass.
  private touchStartDelay = 250;
  // When user starts DnD on touch device it is hard for user to avoid little motion of finger.
  // The following threshold defines allowed distance of motion relative to initial position (initPosition).
  private touchStartPositionThreshold = 16;
  private preventScrollTimeout: any;
  private startDraggingTimeout: any;
  private initPosition: VirtualDndPosition;

  constructor(private ngZone: NgZone) {
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPreventDefault = this.onPreventDefault.bind(this);

  }

  ngOnDestroy(): void {
    this.stopDragging();
  }

  initDragging(itemData: any, element: HTMLElement, event: MouseEvent | TouchEvent): void {
    const pagePosition = getPointerPosition(event);

    this.draggingItemData = itemData;
    this.isDragging = false;
    this.activeZone = void 0;
    this.pointerDownWhen = isTouchEvent(event) ? new Date() : void 0;
    this.initPosition = pagePosition;
    this.cacheBounds(element, pagePosition);
    this.installDragListeners();

    if (isTouchEvent(event)) {
      this.startDraggingTimeout = setTimeout(() => this.startDragging(pagePosition), this.touchStartDelay);
    } else {
      this.startDragging(pagePosition);
    }
  }

  registerZone(data: any, element: HTMLElement): void {
    this.zones.push({ data, element });
  }

  registerViewport(viewport: VirtualDndViewport): void {
    this.viewport = viewport;
  }

  private onPointerMove(event: MouseEvent | PointerEvent): void {
    const pagePosition = getPointerPosition(event);

    if (!this.isDragging) {
      // stop dragging if long press delay is not passed
      if (isTouchEvent(event) && !this.isLongPressDelayPassed()) {
        // we stop dragging only if user exceed allowed position threshold
        if (this.isPointerThresholdExceed(pagePosition)) {
          this.stopDragging();
        }
        return;
      }

      this.startDragging(pagePosition);
    } else {
      this.move(pagePosition);
    }
  }

  private onPointerUp(event: MouseEvent | PointerEvent): void {
    this.drop(getPointerPosition(event));
    this.stopDragging();
  }

  private onPreventDefault(event: WheelEvent): void {
    if (this.isDragging) {
      event.preventDefault();
    }
  }

  private updateActiveZone(pointer: VirtualDndPosition): void {
    const zone = this.findZoneByPointer(pointer);
    if (zone !== this.activeZone) {
      this.ngZone.run(() => {
        if (this.activeZone) {
          this.viewport.zoneExited.emit({
            itemData: this.draggingItemData,
            zoneData: this.activeZone.data,
          });
        }
        this.activeZone = zone;
        if (zone) {
          this.viewport.zoneEntered.emit({
            itemData: this.draggingItemData,
            zoneData: zone.data,
          });
        }
      });
    }
  }

  private installDragListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onPointerMove, passiveCapturingEventOptions);
      document.addEventListener('mouseup', this.onPointerUp, true);

      document.addEventListener('touchmove', this.onPointerMove, passiveEventListenerOptions);
      document.addEventListener('touchend', this.onPointerUp, true);

      document.addEventListener('selectstart', this.onPreventDefault, activeCapturingEventOptions);

      this.preventScrollTimeout = setTimeout(
        () => {
          document.addEventListener('touchmove', this.onPreventDefault, activeCapturingEventOptions);
        },
        this.touchStartDelay / 2);
    });
  }

  private uninstallDragListeners(): void {
    clearTimeout(this.startDraggingTimeout);
    clearTimeout(this.preventScrollTimeout);
    document.removeEventListener('touchmove', this.onPreventDefault, activeCapturingEventOptions);

    document.removeEventListener('selectstart', this.onPreventDefault, activeCapturingEventOptions);

    document.removeEventListener('mousemove', this.onPointerMove, passiveCapturingEventOptions);
    document.removeEventListener('mouseup', this.onPointerUp, true);

    document.removeEventListener('touchmove', this.onPointerMove, passiveEventListenerOptions);
    document.removeEventListener('touchend', this.onPointerUp, true);
  }

  private startDragging(pagePosition: VirtualDndPosition): void {
    clearTimeout(this.startDraggingTimeout);

    const position = this.toViewportPosition(pagePosition);

    this.isDragging = true;
    let previewData = this.draggingItemData;
    this.viewport.dragStarted.emit({
      data: this.draggingItemData,
      setPreviewData(data: any): void {
        previewData = data;
      },
    });
    this.previewEl = this.createPreviewElement(previewData);
    this.setPreviewPosition(pagePosition);
    this.updateActiveZone(position);
    document.body.style.cursor = 'move';
  }

  private move(pagePosition: VirtualDndPosition): void {
    const position = this.toViewportPosition(pagePosition);

    this.setPreviewPosition(pagePosition);
    this.ngZone.run(() => {
      this.viewport.dragMoved.emit({
        data: this.draggingItemData,
        pagePosition,
        viewportPosition: position,
      });
    });
    this.updateActiveZone(position);
  }

  private stopDragging(): void {
    document.body.style.cursor = '';
    this.uninstallDragListeners();
    this.cleanCacheOfBounds();
    this.isDragging = false;
    this.pointerDownWhen = void 0;
    this.activeZone = void 0;
    this.draggingItemData = void 0;
    if (this.previewEl && this.previewEl.parentElement) {
      this.previewEl.parentElement.removeChild(this.previewEl);
    }
    if (this.previewRef) {
      this.previewRef.destroy();
      this.previewRef = void 0;
    }
  }

  private drop(pagePosition: VirtualDndPosition): void {
    const { activeZone } = this;
    const position = this.toViewportPosition(pagePosition);
    this.updateActiveZone(position);
    this.ngZone.run(() => {
      if (activeZone) {
        this.viewport.zoneExited.emit({
          itemData: this.draggingItemData,
          zoneData: activeZone.data,
        });
      }
      this.viewport.dragEnded.emit({
        itemData: this.draggingItemData,
        zoneData: activeZone ? activeZone.data : void 0,
      });
    });
  }

  private cacheBounds(element: HTMLElement, initPosition: VirtualDndPosition): void {
    this.viewportOffset = toPosition(domOffset(this.viewport.element));
    this.elementOffset = shiftPosition(getElementOffset(element, initPosition), DND_OFFSET);
    this.zones.forEach((zone) => {
      const position = domOffsetUntil(zone.element, this.viewport.element);
      const clientRect = zone.element.getBoundingClientRect();
      if (position) {
        zone.box = {
          ...position,
          right: position.left + clientRect.width,
          bottom: position.top + clientRect.height,
        };
      }
    });
  }

  private cleanCacheOfBounds(): void {
    this.zones.forEach((zone) => {
      zone.box = void 0;
    });
  }

  private toViewportPosition(pagePosition: VirtualDndPosition): VirtualDndPosition {
    const { x: viewportLeft, y: viewportTop } = this.viewportOffset;
    const { scrollLeft, scrollTop } = this.viewport.element;
    return {
      x: pagePosition.x - viewportLeft + scrollLeft,
      y: pagePosition.y - viewportTop + scrollTop,
    };
  }

  private createPreviewElement(data: any): HTMLElement {
    const { viewContainerRef, templateRef } = this.viewport.preview;

    const viewRef = viewContainerRef.createEmbeddedView(templateRef, { $implicit: data });
    this.previewRef = viewRef;

    const previewEl = viewRef.rootNodes[0];
    previewEl.style.pointerEvents = 'none';
    previewEl.style.position = 'fixed';
    previewEl.style.top = '0';
    previewEl.style.left = '0';
    previewEl.style.zIndex = '1000';

    previewEl.style.touchAction = '';
    previewEl.style.webkitUserDrag = '';
    previewEl.style.webkitTapHighlightColor = '';
    previewEl.style.userSelect = '';
    previewEl.style.msUserSelect = '';
    previewEl.style.webkitUserSelect = '';
    previewEl.style.MozUserSelect = '';

    document.body.appendChild(previewEl);

    return previewEl;
  }

  private setPreviewPosition(position: VirtualDndPosition): void {
    if (this.previewEl) {
      const { x: offsetX, y: offsetY } = this.elementOffset;
      
      this.previewEl.style.transform = `translate3d(${Math.round(position.x - offsetX)}px, ${Math.round(position.y - offsetY)}px, 0)`;
    }
  }

  private findZoneByPointer({ x, y }: VirtualDndPosition): Maybe<VirtualDndZone> {
    return <any>find(
      this.zones,
      ({ box }) => box && box.left <= x && box.right >= x && box.top <= y && box.bottom >= y);
  }

  private isLongPressDelayPassed(): boolean {
    if (this.pointerDownWhen) {
      return (new Date().getTime() - this.pointerDownWhen.getTime()) >= this.touchStartDelay;
    } else {
      return true;
    }
  }

  private isPointerThresholdExceed(currentPosition: VirtualDndPosition): boolean {
    if (this.touchStartPositionThreshold) {
      const { x: initX, y: initY } = this.initPosition;
      const { x: currentX, y: currentY } = currentPosition;
      const diffX = currentX - initX;
      const diffY = currentY - initY;

      return Math.sqrt(diffX * diffX + diffY * diffY) < this.touchStartPositionThreshold;
    } else {
      return true;
    }
  }
}
